<template>
  <div
    id="map"
    ref="map"
    v-on:mouseenter="mouseover = true"
    v-on:mouseleave="mouseover = false"
  >
    <div class="mapboxgl-ctrl-top-left">
      <div class="name">
        <ShortcutSelect
          v-bind:indFromName="indFromName"
          v-bind:mappedIndicator="mappedIndicator"
          v-bind:normalizationIndicator="normalizationIndicator"
          v-bind:groupNames="groupNames"
          v-bind:groups="groups"
          v-bind:selectedShortcut="selectedShortcut"
          v-bind:inInerMode="inInerMode"
          v-bind:namesFromGeotype="namesFromGeotype"
          v-bind:geotypesForMap="geotypesForMap"
          v-bind:geotype="geotype"
          v-on:set-ind="setIndicator($event)"
          v-on:set-norm="setNormalization($event)"
          v-on:set-map="setMap($event)"
          v-on:set-geo-type="setGeoType($event)"
          v-on:override-classification="overrideClassification = $event"
        />
        <colerLegend
          v-if="loaded"
          v-bind:colorLegendData="colorLegendData"
          v-bind:format="format"
          v-bind:legendMarks="legendMarks"
        />
      </div>
    </div>
    <div class="mapboxgl-ctrl-bottom-right">
      <Legend
        v-if="loaded"
        v-bind:colorLegendData="colorLegendData"
        v-bind:format="format"
        v-bind:legendMarks="legendMarks"
      />
    </div>
    <div id="basemap" class="mapboxgl-ctrl-top-right">
      <div
        class="mapbox-ctrl mapboxgl-ctrl-group basemaps">
        <BaseMapSelect v-on:style-change="setMapStyle($event)"/>
      </div>
    </div>
    <div id="homeExtent" class="mapboxgl-ctrl-top-left">
      <div
        v-on:click="setSelectedtoRegion()"
        class="mapbox-ctrl mapboxgl-ctrl-group">
        <button class="mapboxgl-ctrl-icon" type="button" title="Reset to Region">
          <font-awesome-icon icon="home"/>
        </button>
      </div>
    </div>
    <div ref="mapgl" v-on:mouseleave="setHover(null)"></div>
    <MapboxLayer
      name="communities"
      v-bind:geoJSON="communitiesGeoJSON"
      v-bind:layers="communitiesLayer"
      v-bind:interactivity=true
      v-bind:before-layer-name=beforeLayers
      v-on:featureClick="communityClick($event)"
      v-on:featureMouseMove="communityHover($event)"
      v-on:featureMouseLeave="setHover(null)"
    />
    <Loader v-if="!loaded"/>
  </div>
</template>

<script>
  import debounce from 'lodash.debounce'
  import mapboxgl from 'mapbox-gl'
  import * as d3 from 'd3'
  import ShortcutSelect from './map/ShortcutSelect'
  import MapboxLayer from './map/MapboxLayer'
  import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder'
  import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css'
  import cartocolor from 'cartocolor'
  import * as turf from 'turf'
  import { diff } from 'deep-diff'
  import Loader from './map/Loader'
  import ColerLegend from './map/ColerLegend'
  import Legend from './map/Legend'
  import BaseMapSelect from "./map/BaseMapSelect";
  import { naturalBreaks } from './map/NaturalBreaks'

  export default {
  name: 'MainMap',
  components: {BaseMapSelect, Legend, ColerLegend, Loader, ShortcutSelect, MapboxLayer},
  props: [
    'geotype',
    'allGeojsonByType',
    'mappedIndicator',
    'normalizationIndicator',
    'selectedId',
    'hoverId',
    'filteredCounty',
    'indFromName',
    'getIndMoeName',
    'groupNames',
    'groups',
    'selectedShortcut',
    'inInerMode',
    'namesFromGeotype',
    'geotypesForMap',
  ],
  data: function () {
    return {
      idsInViewport: new Set(),
      loaded: false,
      mouseover: false,
      overrideClassification: null,
    }
  },
  computed: {
    map: function () {
      mapboxgl.accessToken = 'pk.eyJ1Ijoic2VtY29nIiwiYSI6ImlvNEFUcmMifQ.VmDLZZG7l6syscFzkBs5eg'
      let style = './geojson/Community_Boundaries.json'
      return new mapboxgl.Map({
        container: this.$refs.mapgl,
        style,
        center: [-83.2437186609522, 42.454430721108764],
        zoom: 7.684440451829983,
        minZoom: 6,
        scrollZoom: true,
        dragRotate: false,
        attributionControl: false,
      }).addControl(new mapboxgl.NavigationControl(), 'top-left')
        .addControl(new mapboxgl.FullscreenControl({ container: this.$refs.map }))
        .addControl(new mapboxgl.AttributionControl({
          compact: true,
          customAttribution: 'Sources: <a href="https://www.census.gov/programs-surveys/acs" target="_blank">American Community Survey 2020-2024 5-Year Estimates</a>, SEMCOG Employment data 2019.',
        }))
        .addControl(new MapboxGeocoder({
          accessToken: mapboxgl.accessToken,
          bbox: [ -85.11, 41.55, -81.46, 43.32 ],
          zoom: 10,
          collapsed: true,
          marker: true,
          flyTo: false,
          mapboxgl: mapboxgl,
          clearOnBlur: true,
          clearAndBlurOnEsc: true,
        }).on('result', (r) => {
          this.setSelectedFromGeocode(r.result.geometry.coordinates[0], r.result.geometry.coordinates[1])
        }), 'bottom-left')
    },
    communitiesLayer: function () {
      let filter = ['>', this.normalizationIndicator, 0]
      if (this.filteredCounty) {
        filter = ['all', filter, ['any', ['==', 'county', this.filteredCounty], ['==', 'county2', this.filteredCounty]]]
      }
      if (!this.mappedIndicator) {
        return []
      }
      let color = '#fff'
      if (this.breakPoints.length) {
        color = ['step', ['/', ['get', this.mappedIndicator], ['get', this.normalizationIndicator]]]
        for (let i = 0; i < this.breakPoints.length; i += 1) {
          color.push(this.colorPalette[i], this.breakPoints[i])
        }
        color.push(this.colorPalette[this.breakPoints.length])
      }
      return [
        {
          type: 'fill',
          filter: filter,
          paint: {
            'fill-color': color,
            'fill-opacity': 0.8,
          },
        },
        {
          type: 'line',
          filter: filter,
          paint: {
            'line-width': ['case', ['==', ['get', 'geoid'], (this.hoverId || '')], 4.5, ['==', ['get', 'geoid'], (this.selectedId || '')], 4.5, 2],
            'line-color': ['case', ['==', ['get', 'geoid'], (this.hoverId || '')], '#22323b', ['==', ['get', 'geoid'], (this.selectedId || '')], '#dc4915', 'black'],
            'line-blur': 2,
          },
        },
      ]
    },
    beforeLayers: function () {
      return ['us-congress-districts-boundary-lines', 'state-house-boundary-lines', 'state-senate-districts-boundary-lines', 'communities_line_1', 'num_prov_raster_0', 'cartography-county', 'place-village', 'mcd-pop-label-2uiaf9', 'local-dx1f7q', 'Roads by Type/Local Roads/Local Road', 'CommunityBoundary/label/Class 1_1', 'Michigan Counties', 'Railroad/casing', 'all_districts_reprojected/0']
    },
    communitiesGeoJSON: function () {
      return this.allGeojsonByType.get(this.geotype)
    },
    popupLookup: function () {
      if (!this.communitiesGeoJSON) {
        return new Map()
      }
      return new Map(this.communitiesGeoJSON.features.map(f =>
        [
          f.properties.geoid,
          [
            turf.pointOnFeature(f.geometry).geometry.coordinates,
            `<div>${f.properties['name']}</div><div>${this.tooltipFormat(f.properties)}</div>`,
          ],
        ],
      ))
    },
    popupData: function () {
      return this.popupLookup.get(this.hoverId || this.selectedId)
    },
    featuresLookup: function () {
      if (!this.communitiesGeoJSON) {
        return new Map()
      }
      return new Map(this.communitiesGeoJSON.features.map(f =>
        [
          f.properties.geoid,
          f.properties,
        ],
      ))
    },
    rawBreakPoints: function () {
      let raw_values = Array.from(this.idsInViewport.values())
        .map(id => this.featuresLookup.get(id))
        .filter(f => (
          f &&
          f[this.mappedIndicator] &&
          f[this.normalizationIndicator] &&
          f[this.normalizationIndicator] > (f[this.getIndMoeName(this.normalizationIndicator)] || 0)
        ))
      let values = raw_values
        .map(f => (f[this.mappedIndicator] / f[this.normalizationIndicator]))
      let maxvalue = Math.max(0, ...values)
      let minvalue = Math.min(Infinity, ...values)
      let count = values.length || 0
      if (count === 0 || maxvalue === 0) {
        return []
      }

      let raw = []
      let classification = this.overrideClassification || this.selectedShortcut && this.selectedShortcut.classification;
      if (classification === 'divergingRatio') {
        raw = [
          1 - ((1 - minvalue) / 2),
          1,
          ((maxvalue - 1) / 3) + 1,
        ]
      } else if (classification === 'AreaNaturalBreaks') {
        raw = naturalBreaks(values, 4, raw_values.map(f => f.acres))
      } else if (classification === 'AreaLogNaturalBreaks') {
        raw = naturalBreaks(values.map(Math.log1p), 4, raw_values.map(f => f.acres)).map(Math.expm1)
      } else if (classification === 'NaturalBreaks') {
        raw = naturalBreaks(values, 4)
      } else if (classification === 'LogNaturalBreaks') {
        raw = naturalBreaks(values.map(Math.log1p), 4).map(Math.expm1)
      } else if (classification === 'AreaViewportPercentile') {
        let sorted = raw_values.sort((a, b) =>
          (a[this.mappedIndicator] / a[this.normalizationIndicator]) -
          (b[this.mappedIndicator] / b[this.normalizationIndicator])
        )
        let cumsumAcres = sorted.map((sum => value => sum += value.acres)(0))
        let totalAcres = cumsumAcres[cumsumAcres.length - 1]
        let percentiles = [0.05, 0.30, 0.70, 0.95]
        raw = percentiles.map(v => {
          let i = cumsumAcres.findIndex(s => s >= (v * totalAcres)) // TODO O(n^2)
          return sorted[i][this.mappedIndicator] / sorted[i][this.normalizationIndicator]
        })
      } else {
        // defeat to viewportPercentile if custom map

        let sorted = values.sort((a, b) => a - b)
        let percentiles = [0.05, 0.30, 0.70, 0.95]
        raw = percentiles.map(v => sorted[Math.floor(v * count)])
      }
      return raw
    },
    breakPoints: function () {
      let out = []
      let max = -Infinity
      this.rawBreakPoints.forEach(b => {
        if (b > max) {
          out.push(b)
          max = b
        }
      })
      return out
    },
    colorPalette: function () {
      if (!this.selectedShortcut){
        return cartocolor['Emrld'][this.breakPoints.length + 1] || []
      }
      if (cartocolor[this.selectedShortcut.cartoColor]) {
        return cartocolor[this.selectedShortcut.cartoColor][this.breakPoints.length + 1] || []
      }
      let out = this.selectedShortcut.cartoColor.split(',') || [];
      while (out.length <= this.breakPoints.length) {
        out.push('#000')
      }
      return out
    },
    colorLegendData: function () {
      let out = []
      for (let i = 0; i <= this.breakPoints.length; i += 1) {
        out.push(
          {
            key: [
              this.breakPoints[i - 1] || -Infinity,
              this.breakPoints[i] || Infinity,
            ],
            value: this.colorPalette[i],
          }
        )
      }
      return out
    },
    popup: function () {
      return new mapboxgl.Popup({
        closeButton: false,
        closeOnClick: false,
      })
    },
    format: function () {
      return d3.format((this.selectedShortcut && this.selectedShortcut.D3FormatterString) || '.2f')
    },
    maxMapValue: function () {
      if (!this.communitiesGeoJSON) {
        return 0
      }
      return Math.max(0, ...this.communitiesGeoJSON.features.map(f => f.properties)
        .filter(f => (
          f[this.mappedIndicator] &&
          f[this.normalizationIndicator] &&
          f[this.normalizationIndicator] > (f[this.getIndMoeName(this.normalizationIndicator)] || 0)
        ))
        .map(f => f[this.mappedIndicator] / f[this.normalizationIndicator]))
    },
    legendMarks: function () {
      let d = [0].concat(this.colorLegendData.map(legend => legend.key[1]).filter(isFinite)).concat([this.maxMapValue])
      return Array.from(new Set(d))
    },
  },
  methods: {
    setIndicator: function (id) {
      this.$emit('set-ind', id)
    },
    setHover: debounce(function (id) {
      if (this.mouseover || !id) {
        if (this.hoverId !== id) {
          this.$emit('set-hov-geom-id', id)
        }
      }
    }, 100),
    setNormalization: function (id) {
      this.$emit('set-norm', id)
    },
    setMap: function (id) {
      this.$emit('set-map', id)
    },
    setGeoType: function (id) {
      this.$emit('set-geo-type', id)
    },
    setSelectedtoRegion: function () {
      this.setSelected(8999)
      this.zoomTo(8999)
    },
    setSelected: function (id) {
      this.$emit('set-sel-geom-id', id)
    },
    communityClick: function (featureEvent) {
      if (featureEvent.features.length > 0) {
        const feature = featureEvent.features[0]
        this.$emit('set-sel-geom-id', feature.properties.geoid)
      }
    },
    communityHover: function (featureEvent) {
      if (featureEvent.features.length > 0) {
        const feature = featureEvent.features[0]
        this.setHover(feature.properties.geoid)
      } else {
        this.setHover(null)
      }
    },
    setMapStyle: function (e) {
      this.map.setStyle(e)
    },
    setStyle: debounce(function () {
      if (!this.map.getLayer('communities_fill_0')) {
        return
      }

      let features = this.map.queryRenderedFeatures({layers: ['communities_fill_0']});
      // queryRenderedFeatures can return duplicates so filter them out
      features = Object.freeze(new Set(features.map(f => f.properties.geoid)));
      if (
        features.size > 0 && (
          features.size !== this.idsInViewport.size ||
          Array.from(features.keys()).some(id => !this.idsInViewport.has(id))
        )
      ) {
        this.idsInViewport = features
        this.$nextTick(() => {
          this.loaded = true
        })
      }
    }, 150, { leading: true, maxWait: 600 }),
    zoomTo: function (id) {
      if (!this.communitiesGeoJSON) {
        return
      }
      let extent = {
        93: [[-84.2579218629181, 42.3239034763136], [-83.564870927822, 42.8832526817047]],
        99: [[-83.202747340568, 42.3471894195991], [-82.6063045158249, 42.9974602667658]],
        115: [[-83.87391435586, 41.6235951031165], [-83.0785807251129, 42.1942805362744]],
        125: [[-83.7894115599797, 42.3312327335914], [-82.9834924191637, 42.9886278835273]],
        147: [[-83.0960949946948, 42.418557292642], [-82.3184528982991, 43.2688321383929]],
        161: [[-84.2340738132845, 41.9715949343173], [-83.4394579054165, 42.5351067992344]],
        163: [[-83.6516108768434, 41.9331541851303], [-82.7701109750752, 42.5510028516089]],
      }[id]
      if (!extent) {
        let geo = this.communitiesGeoJSON.features.find(geo => geo.properties.geoid === id)
        if (geo) {
          let bbox = turf.bbox(turf.buffer(
            turf.bboxPolygon(turf.bbox(geo)),
            17 + 0.0000004 * Math.sqrt(geo.properties.acres || 0), //TODO: tune to match existing behaviour
            { units: 'kilometers', steps: 4 },
          ))
          extent = [[bbox[0], bbox[1]], [bbox[2], bbox[3]]]
        }
      }
      if (!extent) {
        extent = [[-84.39220211103182, 41.29773946024838], [-82.09523521088468, 43.59014664194771]]
      }
      this.map.fitBounds(
        extent,
        { padding: 1 },
      )
    },
    setSelectedFromGeocode: function (x, y) {
      if (!this.map.getLayer('communities_fill_0')) {
        return
      }
      let features = this.map.queryRenderedFeatures(this.map.project([x, y]), { layers: ['communities_fill_0'] });
      if (features.length) {
        this.setSelected(features[0].properties.geoid)
      }
    },
    tooltipFormat: function (properties) {
      let denom = properties[this.normalizationIndicator]
      let denomMoe = properties[this.getIndMoeName(this.normalizationIndicator)] || 0
      let noom = properties[this.mappedIndicator]
      let noomMoe = properties[this.getIndMoeName(this.mappedIndicator)] || 0
      let value = noom / denom

      // MOE of X/Y =1/Y * SQRT ((XMOE)2- ((X/Y)2*(YMOE)2))
      let a = Math.pow(noomMoe, 2)
      let b = Math.pow(value * denomMoe, 2)
      let moe
      if (a > b && this.selectedShortcut && this.selectedShortcut.properFraction) {
        moe = Math.sqrt(a - b) / denom
      } else {
        moe = Math.sqrt(a + b) / denom
      }

      let maxMoe
      if (this.selectedShortcut && this.selectedShortcut.properFraction) {
        maxMoe = 1
      } else {
        maxMoe = 2 * value
      }

      let moeMin = Math.max(value - moe, 0)
      let moeMax = Math.min(value + moe, maxMoe)
      let delta = moeMax - moeMin
      if (delta === 0) {
        return '<strong>' + this.format(value) + '</strong>'
      } else {
        return '<strong>' + this.format(value) + '</strong><small> +/- ' + this.format(delta / 2) + '</small>'
      }
    },
  },
  mounted: function () {
    this.$once('hook:beforeDestroy', function () {
      this.map.remove()
    })
    this.map.once('idle', () => {
      this.setStyle();
      this.zoomTo(this.selectedId || this.filteredCounty)
      this.map.on('sourcedata', (e) => {
        if (e.sourceId === 'communities') {
          this.setStyle();
        }
      });
    });
  },
  watch: {
    selectedId: function () {
      this.zoomTo(this.selectedId || this.filteredCounty)
    },
    popupData: function () {
      let data = this.popupData
      if (data) {
        this.popup.setLngLat(data[0])
        this.popup.setHTML(data[1])
        this.popup.addTo(this.map)
      } else {
        this.popup.remove()
      }
    },
    filteredCounty: function () {
      this.zoomTo(this.filteredCounty)
    },
    colorLegendData: function (oldColorLegendData, newColorLegendData) {
      let changes = diff(oldColorLegendData, newColorLegendData)
      if (changes) {
        this.$emit('set-legend-color-data', this.colorLegendData)
      }
    },
  },
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only. /deep/ applies to children as well -->
<style scoped>
  #map {
    grid-column: 1;
    grid-row: 2;
    position: relative;
  }

  /deep/ .mapboxgl-map {
    width: 100%;
    height: 100%;
  }

  .mapboxgl-ctrl-top-right {
    max-width: calc(100% - 50px);
  }

  .mapboxgl-ctrl-top-right > div.basemaps {
    background-color: white;
    opacity: 0.9;
    padding: 0;
    margin: 50px 10px 0 0;
    pointer-events: auto;
    border-radius: 2px;
  }

  .mapboxgl-ctrl-top-left {
    max-width: calc(100% - 50px);
  }

  .mapboxgl-ctrl-top-left>div.name {
    background-color: white;
    opacity: 0.9;
    padding: 7px;
    margin: 10px 0 0 50px;
    pointer-events: auto;
    border-radius: 15px;
  }

  .mapboxgl-ctrl-bottom-right {
    max-width: calc(100% - 250px);
  }

  #homeExtent {
    margin: 110px 0px 10px 10px;
    pointer-events: auto;
    position: absolute
  }

  /deep/ .mapboxgl-popup {
    display: block !important;
    z-index: 1;
    pointer-events: none !important;
  }

  /deep/ .mapboxgl-popup strong {
    font-size: medium;
  }

  /deep/ .mapboxgl-popup small {
    color: #9aa5a9;
    font-size: small;
  }

  /deep/ .mapboxgl-popup .mapboxgl-popup-content {
    background: #292424;
    color: white;
    opacity: 0.95;
    border-radius: 16px;
    padding: 5px 10px 4px;
    pointer-events: none !important;
  }

  /deep/ .mapboxgl-popup .mapboxgl-popup-tip {
    width: 0;
    height: 0;
    border-style: solid;
    position: absolute;
    margin: 5px;
    border-color: #292424;
    opacity: 0.8;
    z-index: 1;
  }

  /deep/ .mapboxgl-popup {
    margin-bottom: 10px;
  }

  /deep/ .mapboxgl-popup .mapboxgl-popup-tip {
    border-width: 5px 5px 0 5px;
    border-left-color: transparent !important;
    border-right-color: transparent !important;
    border-bottom-color: transparent !important;
    bottom: -5px;
    left: calc(50% - 10px);
    margin-top: 0;
    margin-bottom: 0;
  }

  @media (max-width: 1300px) {
    #map {
      display: grid;
      grid-template-rows: max-content auto;
    }

    .mapboxgl-ctrl-top-left {
      position: inherit;
      max-width: 100%;
    }

    #homeExtent {
      margin: 110px 0 10px 10px;
      grid-row: 2;
    }

    #basemap {
      grid-row: 2;
    }

    .mapboxgl-ctrl-top-left>div.name {
      margin: 0;
      border-radius: 0;
    }
  }

  @media (max-width: 1000px) {
    .mapboxgl-ctrl-top-left>div.name {
      padding: 10px 10px 5px;
    }
  }

  @media (max-width: 576px), print {
    #map {
      height: 75vh;
      grid-row: 3;
    }
  }
</style>
